<template>
  <outlined-block title="Customer specific pricing" :rules="[valid,pricingValid]" :disabled="disabled" @click="selectedIndex = -1" v-if="customerAccess">
    <v-form v-model="valid" ref="form">
      <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                    :items="customers" no-data-text="No customer prices" class="no-sort"
                    :headers="[{text:'',value:'add',width:'5%',align:'center',cellClass:'pa-0',divider:true},
                               {text: 'Category', value: 'category', width:'10%',align:'center',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                               {text: 'Customer / Category', value: 'customer', width:'35%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                               {text: 'Item #', value: 'item' ,width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                               {text: units + ' price', value: 'price' ,width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt',align:'right'},
                               {text: 'Tax', value: 'taxes' ,width:'20%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'}]"
                    @click.native.stop=""
                    :item-class="item => customers.indexOf(item)=== selectedIndex ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                    @click:row="item => {selectedIndex = customers.indexOf(item); $nextTick(() => this.$refs.form.validate())}">
        <template v-slot:item.add="{index}">
          <v-btn class="mt-n2" icon @click.stop="deleteCustomer(index)" :disabled="disabled"><v-icon small>mdi-delete</v-icon></v-btn>
        </template>
        <template v-slot:item.category="{item,index}">
          <v-simple-checkbox v-if="selectedIndex === index" v-model="item.category" class="mt-n5" @click="item.customerId=''"/>
          <v-icon v-else-if="item.category">mdi-check</v-icon>
        </template>
        <template v-slot:item.customer="{item,index}">
          <template v-if="selectedIndex === index">
            <inventory-categories-select v-if="item.category" v-model="item.customerId" type="Customer" label="Category"/>
            <v-autocomplete v-else dense label="Customer" v-model="item.customerId" :items="allCustomers" item-value="id" item-text="name" class="text-body-2" no-data-text="No customers found" :rules="[fieldRules.required]" outlined>
              <template v-slot:prepend-item>
                <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="customersItem=item;customerCreate=true">
                  <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new customer
                </div>
              </template>
            </v-autocomplete>
          </template>
          <div v-else class="ml-1" :class="{'error--text':!item.customerId}">
            <template v-if="!item.customerId">Required</template>
            <template v-else-if="item.category">{{categoryName(item.customerId)}}</template>
            <template v-else>{{customerName(item.customerId)}}</template>
          </div>
        </template>
        <template v-slot:item.item="{item,index}">
          <v-text-field v-if="selectedIndex === index" dense label="Item #" outlined v-model="item.sku" class="text-body-2" :rules="[fieldRules.maxLength(20)]"/>
          <div v-else class="ml-1">{{item.sku}}</div>
        </template>
        <template v-slot:item.price="{item,index}">
          <decimal-number-input v-if="selectedIndex === index" label="Price" outlined v-model="item.price" :precision="4" class="text-body-2"/>
          <div v-else class="mr-1" :class="{'error--text':item.price < 0}">${{item.price | number(4)}}</div>
        </template>
        <template v-slot:item.taxes="{item,index}">
          <v-autocomplete v-if="selectedIndex === index" dense label="Taxes" outlined v-model="item.taxId" :items="taxes" item-value="id" item-text="name" class="text-body-2"/>
          <div v-else class="ml-1">{{taxName(item.taxId)}}</div>
        </template>
        <template v-slot:body.append="{headers}">
          <tr class="no-hover" @click="selectedIndex = -1">
            <td class="text-center">
              <v-btn fab color="primary" x-small @click.stop="addCustomer" :disabled="disabled"><v-icon small>mdi-plus</v-icon></v-btn>
            </td>
            <td :colspan="headers.length-1"/>
          </tr>
        </template>
      </v-data-table>
    </v-form>
    <customer-quick-create v-model="customerCreate" @create="newCustomer"/>
  </outlined-block>
</template>

<script>
import CustomerQuickCreate from '@/views/sales/customers/CustomerQuickCreate'
import InventoryCategoriesSelect from '@/views/inventory/categories/InventoryCategoriesSelect'

export default {
  name: 'CustomerPricing',
  components: {CustomerQuickCreate,InventoryCategoriesSelect},
  props: {
    disabled: {type:Boolean,required:false,default:false},
    customers: {type:Array,required:true},
    taxes: {type:Array, required:true},
    defaultSku: {type:String, required:false, default:''},
    defaultPrice: {type:Number, required:false, default:0},
    defaultTaxId: {type:String, required:false, default:''},
    units: {type:String,required:false, default:'Unit'}
  },
  data() {
    return {
      valid: true,
      allCustomers: [],
      categories: [],
      customersItem: null,
      customerCreate: false,
      selectedIndex: -1
    }
  },
  computed: {
    pricingValid() {
      return this.customers.every(customer => !!customer.customerId && customer.price >= 0);
    },
    customerAccess() {
      return this.$store.getters.company.permissions.includes('selling');
    }
  },
  async created() {
    if (!this.customerAccess)
      return;
    const data = await Promise.all([
      this.$store.dispatch('customers', true),
      this.$lazyListLoad(this.categories, 'categories2', 'id,name', 100, [], [{name:'type',value:'Customer'}])
    ]);
    this.allCustomers = data[0];
  },
  methods: {
    customerName(customerId) {
      return this.allCustomers.find(customer => customer.id === customerId)?.name;
    },
    taxName(taxId) {
      return this.taxes.find(tax => tax.id === taxId)?.name;
    },
    categoryName(categoryId) {
      return this.categories.find(category => category.id === categoryId)?.name;
    },
    async newCustomer(customer) {
      this.allCustomers = await this.$store.dispatch('customers', true);
      this.customersItem.customerId = customer.id;
    },
    addCustomer() {
      this.customers.push({category:false,customerId:'',sku:this.defaultSku,price:this.defaultPrice,taxId:this.defaultTaxId});
      this.selectedIndex = this.customers.length  - 1;
      this.$nextTick(() => this.$refs.form.validate())
    },
    deleteCustomer(index) {
      this.customers.splice(index, 1);
      this.selectedIndex = - 1;
    }
  }
}
</script>